import { useCallback, useEffect, useRef, useState } from "react";
import { AgentMessagingService } from "../services/AgentMessagingService";
import { FUNCTIONS_BASE_URL, getAuthToken } from "../services/firebase";
import { ChatMessage } from "../types";
import { useConnectionMonitor } from "./useConnectionMonitor";

interface TypingUser {
  userId: string;
  name: string;
  type: "user" | "ai";
  timestamp: Date;
}

export const useRealtimeChat = (agentId: string) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [typingUsers, setTypingUsers] = useState<TypingUser[]>([]);
  const [eventSource, setEventSource] = useState<EventSource | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [sending, setSending] = useState(false);

  // Connection monitoring
  const connectionMonitor = useConnectionMonitor({
    onConnectionLost: () => {
      console.warn("Chat connection lost, attempting to reconnect...");
    },
    onConnectionRestored: () => {
      console.log("Chat connection restored");
    },
    onMaxReconnectAttemptsReached: () => {
      console.error("Max reconnection attempts reached for chat");
      // Trigger a full refresh
      setRefreshTrigger((prev) => prev + 1);
    },
  });

  // Store connection monitor callbacks in refs to avoid re-running effect
  const connectionMonitorRef = useRef(connectionMonitor);
  useEffect(() => {
    connectionMonitorRef.current = connectionMonitor;
  }, [connectionMonitor]);

  // Load initial messages when agent changes
  useEffect(() => {
    if (!agentId) {
      setMessages([]);
      return;
    }

    const loadInitialMessages = async () => {
      const token = await getAuthToken();
      if (!token) {
        console.error("No authentication token available");
        return;
      }

      try {
        const response = await fetch(`${FUNCTIONS_BASE_URL}/getThreadMessages?agentId=${agentId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.messages) {
            // Sort messages by timestamp with latest at bottom
            const sortedMessages = data.messages.sort((a: ChatMessage, b: ChatMessage) => {
              const timeA = typeof a.timestamp === "string" ? new Date(a.timestamp).getTime() : a.timestamp instanceof Date ? a.timestamp.getTime() : (a.timestamp as any).toDate?.()?.getTime() || 0;
              const timeB = typeof b.timestamp === "string" ? new Date(b.timestamp).getTime() : b.timestamp instanceof Date ? b.timestamp.getTime() : (b.timestamp as any).toDate?.()?.getTime() || 0;
              return timeA - timeB; // Ascending order (oldest first, latest last)
            });
            setMessages(sortedMessages);
          }
        }
      } catch (error) {
        console.error("Error loading existing messages:", error);
      }
    };

    loadInitialMessages();
  }, [agentId, refreshTrigger]);

  // Connect to message stream
  useEffect(() => {
    if (!agentId) return;

    let currentEventSource: EventSource | null = null;

    const connectToStream = async () => {
      // Close existing connection if any
      if (currentEventSource) {
        currentEventSource.close();
      }

      const token = await getAuthToken();
      if (!token) {
        console.error("No authentication token available");
        return;
      }

      const newEventSource = new EventSource(`${FUNCTIONS_BASE_URL}/chatStream/${agentId}?token=${encodeURIComponent(token)}`);
      currentEventSource = newEventSource;
      setEventSource(newEventSource);

      newEventSource.onopen = () => {
        connectionMonitorRef.current.handleConnectionOpen();
      };

      newEventSource.onerror = () => {
        connectionMonitorRef.current.handleConnectionError();
      };

      newEventSource.onmessage = (event) => {
        const data = JSON.parse(event.data);

        switch (data.type) {
          case "connected":
            // Connection established
            break;

          case "new_message":
            setMessages((prev) => {
              // Check if message already exists
              const existingIndex = prev.findIndex((msg) => msg.id === data.message.id);

              let updatedMessages;
              if (existingIndex >= 0) {
                // Update existing message
                updatedMessages = [...prev];
                updatedMessages[existingIndex] = data.message;
              } else {
                // Add new message
                updatedMessages = [...prev, data.message];
              }

              // Sort to maintain chronological order
              return updatedMessages.sort((a, b) => {
                const timeA = typeof a.timestamp === "string" ? new Date(a.timestamp).getTime() : a.timestamp instanceof Date ? a.timestamp.getTime() : (a.timestamp as any).toDate?.()?.getTime() || 0;
                const timeB = typeof b.timestamp === "string" ? new Date(b.timestamp).getTime() : b.timestamp instanceof Date ? b.timestamp.getTime() : (b.timestamp as any).toDate?.()?.getTime() || 0;
                return timeA - timeB; // Ascending order (oldest first, latest last)
              });
            });
            break;

          case "typing_update":
            setTypingUsers((prev) => {
              // Only update if the typing users have actually changed
              if (JSON.stringify(prev) !== JSON.stringify(data.typingUsers)) {
                return data.typingUsers;
              }
              return prev;
            });
            break;

          case "ping":
            // Handle ping from server
            connectionMonitorRef.current.handlePing();
            break;

          case "error":
            console.error("Chat stream error:", data.message);
            break;
        }
      };
    };

    connectToStream();

    return () => {
      if (currentEventSource) {
        currentEventSource.close();
      }
      setEventSource(null);
    };
  }, [agentId, refreshTrigger]);

  // Send message
  const sendMessage = useCallback(
    async (content: string) => {
      setSending(true);
      try {
        // Send message (no typing indicators for user)
        await AgentMessagingService.sendUserMessageToAgent(agentId, content);
      } finally {
        setSending(false);
      }
    },
    [agentId]
  );

  // Refresh function
  const refresh = useCallback(() => {
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  return {
    messages,
    typingUsers,
    isConnected: connectionMonitor.status.isConnected,
    sendMessage,
    refresh,
    sending,
  };
};
